import React, { useCallback, useMemo, useRef } from 'react';
import { LatestSplit, Member } from 'types/splits.types';
import { ContributionClassRolesDDL } from 'types/contribution';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { Td, Tr } from 'app/components/Table';
import { Text } from '@chakra-ui/react';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { sumMembersShares } from '../utils';
import { LatestSplitListHeading } from '../LatestSplitListHeading';
import { LatestSplitRow } from './LatestSplitRow';
import { getColumns } from './utils';

export interface LatestSplitListProps {
  isLoading: boolean;
  split?: LatestSplit;
  proposeNewSplit: () => void;
  acceptSplit: () => void;
  downloadSplitPdf: () => Promise<void>;
  currentUserGuid?: string;
  contributionRolesDDL?: ContributionClassRolesDDL;
}
export const LatestSplitList: React.FC<LatestSplitListProps> = ({
  isLoading,
  split,
  proposeNewSplit,
  acceptSplit,
  currentUserGuid,
  contributionRolesDDL,
  downloadSplitPdf,
}) => {
  const sharesSum = useMemo(() => sumMembersShares(split?.members || []), [
    split,
  ]);
  const ref = useRef<HTMLDivElement>(null);
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ totalWidth }), [totalWidth]);

  const rowRenderer = useCallback(
    (row: Row<Member>) => {
      return (
        <LatestSplitRow
          key={row.id}
          split={split}
          member={row.original}
          acceptSplit={acceptSplit}
          proposeNewSplit={proposeNewSplit}
          contributionRolesDDL={contributionRolesDDL}
          currentUserGuid={currentUserGuid}
        />
      );
    },
    [acceptSplit, contributionRolesDDL, currentUserGuid, proposeNewSplit, split]
  );

  return (
    <>
      <LatestSplitListHeading
        downloadSplitPdf={downloadSplitPdf}
        isLoading={isLoading}
        split={split}
      />
      <StickyTable<Member>
        tableKey="latest-splits"
        ref={ref}
        data={split?.members || []}
        columns={columns}
        rowRenderer={rowRenderer}
        loading={isLoading}
        footer={
          <Tr
            sx={{
              ' div': {
                bg: 'M.30',
              },
            }}
          >
            <Td />
            <Td />
            <Td
              sx={{ fontVariantNumeric: 'normal' }}
              data-testid="latest-split-share-total"
            >
              <Text as="span" fontWeight={600}>
                Total:
              </Text>{' '}
              {sharesSum}%
            </Td>
            <Td />
            <Td />
            <Td />
          </Tr>
        }
      />
    </>
  );
};
